import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { onlineshopApiBaseUrl } from '../Filters';
import FilterWrapper from '../../components/FilterWrapper/FilterWrapper';
import { ICategoryFilterData } from '../../../../../Interface/ProductsFilterInterface';
import { useProductsFilter } from '../../../../../Context/ProductsFilterContext';
import CategoryFilterItem from './CategoryFilterItem/CategoryFilterItem';
import useViewports from '../../../../../Hooks/useViewports';

const filterTarget = [
  'brand_ids',
  'sale_percent',
  'sale_type',
  'has_stock',
  'product_ids',
  'product_size_ids',
  'product_attribute_ids',
  'q',
  'favs_only',
];

const CategoryFilter = () => {
  const { xs, sm, md } = useViewports();
  const isMobile = xs || sm || md;
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { filters, registerActiveFilterLabels } = useProductsFilter();
  const selectedCategoryIds = filters.category_ids;
  const selectedSubCategoryIds = filters.sub_category_ids;
  const onlyRealFiltersExceptItself = new URLSearchParams();

  filterTarget.map((filterKey) => {
    const val = searchParams.get(filterKey);
    if (val) onlyRealFiltersExceptItself.set(filterKey, val);
  });

  onlyRealFiltersExceptItself.set(
    'context[]',
    filters.view === 'grid'
      ? 'category_product_sizes_count'
      : 'category_attributes_count'
  );

  onlyRealFiltersExceptItself.append(
    'context[]',
    filters.view === 'grid'
      ? 'sub_category_product_sizes_count'
      : 'sub_category_attributes_count'
  );

  const categoryFilter = useQuery<ICategoryFilterData[]>({
    queryKey: [
      'category_ids',
      onlyRealFiltersExceptItself.toString(), //refetch trigger only when view changes (grid & list) no need for table view refetch because list and table have the same data
      filters.view === 'grid' ? 'grid' : 'list',
    ],
    queryFn: async () => {
      const res = await axios.get(
        `${onlineshopApiBaseUrl}/categories?context[]=sub_categories&${onlyRealFiltersExceptItself.toString()}&sortBy=name`
      );
      return res.data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
  const { isFetching, isSuccess, isPending, data } = categoryFilter;

  const selectedCategoriesQuery = useQuery<ICategoryFilterData[]>({
    queryKey: ['category_ids', 'selected', selectedCategoryIds.join(',')],
    queryFn: async () => {
      const res = await axios.get(
        `${onlineshopApiBaseUrl}/categories?context[]=sub_categories&category_ids=${selectedCategoryIds.join(
          ','
        )}`
      );
      return res.data;
    },
    enabled: selectedCategoryIds.length > 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const selectedCategoryData = selectedCategoriesQuery.data ?? [];

  const mergedOptions = useMemo<ICategoryFilterData[]>(() => {
    const options = new Map<number, ICategoryFilterData>();

    (data ?? []).forEach((option) => {
      options.set(option.erp_id, option);
    });

    // Keep already selected categories visible even if filtered out by brands or sales.
    filters.category_ids.forEach((idString) => {
      const idNumber = Number(idString);
      if (options.has(idNumber)) {
        return;
      }

      const cachedMatch = queryClient
        .getQueriesData<ICategoryFilterData[]>({ queryKey: ['category_ids'] })
        .map(([, cachedData]) =>
          cachedData?.find(
            (cachedCategory) => cachedCategory.erp_id === idNumber
          )
        )
        .find(Boolean);

      if (cachedMatch) {
        options.set(idNumber, cachedMatch);
        return;
      }

      const selectedMatch = selectedCategoryData.find(
        (selectedCategory) => selectedCategory.erp_id === idNumber
      );
      if (selectedMatch) {
        options.set(idNumber, {
          ...selectedMatch,
          product_sizes_count: 0,
          sub_categories: selectedMatch.sub_categories ?? [],
        });
        return;
      }

      options.set(idNumber, {
        erp_id: idNumber,
        id: idNumber,
        name: idString,
        product_sizes_count: 0,
        sub_categories: [],
      });
    });

    return Array.from(options.values());
  }, [data, filters.category_ids, queryClient, selectedCategoryData]);

  useEffect(() => {
    if (mergedOptions.length === 0) return;

    if (selectedCategoryIds.length > 0) {
      const categoryLabels = selectedCategoryIds
        .map((id) => {
          const option = mergedOptions.find(
            (opt) => opt.erp_id.toString() === id.toString()
          );
          if (option) {
            return { value: id, label: option.name };
          }
          return null;
        })
        .filter(
          (item): item is { value: string; label: string } => item !== null
        );

      if (categoryLabels.length > 0) {
        registerActiveFilterLabels('category_ids', categoryLabels);
      }
    }

    if (selectedSubCategoryIds.length > 0) {
      const subCategoryLabels: { value: string; label: string }[] = [];

      mergedOptions.forEach((category) => {
        category.sub_categories?.forEach((subCat) => {
          if (selectedSubCategoryIds.includes(subCat.erp_id.toString())) {
            subCategoryLabels.push({
              value: subCat.erp_id.toString(),
              label: subCat.name,
            });
          }
        });
      });

      if (subCategoryLabels.length > 0) {
        registerActiveFilterLabels('sub_category_ids', subCategoryLabels);
      }
    }
  }, [
    mergedOptions,
    selectedCategoryIds,
    selectedSubCategoryIds,
    registerActiveFilterLabels,
  ]);

  if (isSuccess && !isPending && !mergedOptions.length) return null;

  return (
    <FilterWrapper
      label="category"
      activeFilterKeys={['category_ids', 'sub_category_ids']}
      {...{
        isFetching,
        isSuccess,
        isPending,
        resultsCount: mergedOptions.length,
        initialOpen: isMobile ? false : true,
        expandable: isMobile ? true : false,
      }}
    >
      {mergedOptions.map((filterOption) => (
        <CategoryFilterItem
          key={`${filterOption.name}-${filterOption.erp_id}`}
          category={filterOption}
        />
      ))}
    </FilterWrapper>
  );
};

export default CategoryFilter;
